import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAPR } from '../context/APRContext';

const MasterData: React.FC = () => {
  const navigate = useNavigate();
  const { state } = useAPR();
  const { dashboardStats, currentRunRateData, applications } = state;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  // Calculate additional statistics
  const totalApplicationsWithCosts = currentRunRateData.length;
  // Count only child applications (exclude parents) for total applications
  const parentApplicationsCount = applications.filter(app => app.isParent).length;
  const childApplicationsCount = applications.filter(app => !app.isParent).length;
  const totalApplicationsInSystem = childApplicationsCount;
  const costCoverage = totalApplicationsInSystem > 0 
    ? Math.round((totalApplicationsWithCosts / totalApplicationsInSystem) * 100) 
    : 0;

  const avgCostPerApp = totalApplicationsWithCosts > 0 
    ? dashboardStats.totalCosts.current_run_rate / totalApplicationsWithCosts 
    : 0;

  // Find highest and lowest cost applications
  const highestCostApp = currentRunRateData.reduce((max, app) => 
    (!max || app.totalAnnualCost > max.totalAnnualCost) ? app : max, 
    currentRunRateData[0]
  );

  const lowestCostApp = currentRunRateData.reduce((min, app) => 
    (!min || app.totalAnnualCost < min.totalAnnualCost) ? app : min, 
    currentRunRateData[0]
  );

  return (
    <div className="page">
      {/* Header */}
      <div className="page-header">
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Master Data Overview</h1>
            <p className="page-header__subtitle">
              Comprehensive view of application portfolio data and cost analysis
            </p>
          </div>
          <div className="page-header__actions">
          </div>
        </div>
      </div>

      {/* Main Content Container */}
      <div className="master-data-container">

      <div className="dashboard-grid">
        {/* Application Statistics */}
        <div className="dashboard-section">
          <h2>Application Portfolio Statistics</h2>
          <div className="card-grid">
            <div className="overview-card">
              <div className="card-header">
                <h3>Total Applications</h3>
                <span className="card-icon">📱</span>
              </div>
              <div className="card-value">{totalApplicationsInSystem}</div>
              <div className="card-subtitle">Child applications in system</div>
            </div>
            <div className="overview-card">
              <div className="card-header">
                <h3>Parent Applications</h3>
                <span className="card-icon">🏢</span>
              </div>
              <div className="card-value">{parentApplicationsCount}</div>
              <div className="card-subtitle">Parent application groups</div>
            </div>
            <div className="overview-card">
              <div className="card-header">
                <h3>Applications with Cost Data</h3>
                <span className="card-icon">💰</span>
              </div>
              <div className="card-value">{totalApplicationsWithCosts}</div>
              <div className="card-subtitle">{costCoverage}% coverage</div>
            </div>
          </div>
        </div>

        {/* Cost Statistics */}
        <div className="dashboard-section">
          <h2>Cost Analysis Summary</h2>
          <div className="cost-grid">
            <div className="cost-card">
              <div className="cost-header">
                <h3>Total Annual Costs</h3>
                <span className="cost-icon">💰</span>
              </div>
              <div className="cost-value">
                {formatCurrency(dashboardStats.totalCosts.current_run_rate)}
              </div>
              <div className="cost-subtitle">Current run rate across portfolio</div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Average Cost per App</h3>
                <span className="cost-icon">📊</span>
              </div>
              <div className="cost-value">
                {formatCurrency(avgCostPerApp)}
              </div>
              <div className="cost-subtitle">Mean annual cost</div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Highest Cost Application</h3>
                <span className="cost-icon">⬆️</span>
              </div>
              <div className="cost-value">
                {formatCurrency(highestCostApp?.totalAnnualCost || 0)}
              </div>
              <div className="cost-subtitle">{highestCostApp?.applicationName || 'N/A'}</div>
            </div>
            <div className="cost-card">
              <div className="cost-header">
                <h3>Lowest Cost Application</h3>
                <span className="cost-icon">⬇️</span>
              </div>
              <div className="cost-value">
                {formatCurrency(lowestCostApp?.totalAnnualCost || 0)}
              </div>
              <div className="cost-subtitle">{lowestCostApp?.applicationName || 'N/A'}</div>
            </div>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div className="dashboard-section">
          <h2>Future Cost Projections</h2>
          <div className="card-grid">
            <div className="overview-card">
              <div className="card-header">
                <h3>Planned Investment</h3>
                <span className="card-icon">📋</span>
              </div>
              <div className="card-value">{formatCurrency(dashboardStats.totalCosts.planned_cost)}</div>
              <div className="card-subtitle">One-time improvement costs</div>
            </div>
            <div className="overview-card">
              <div className="card-header">
                <h3>Migration Costs</h3>
                <span className="card-icon">🔄</span>
              </div>
              <div className="card-value">{formatCurrency(dashboardStats.totalCosts.migration_conversion)}</div>
              <div className="card-subtitle">Rationalization implementation</div>
            </div>
            <div className="overview-card">
              <div className="card-header">
                <h3>Future Run Rate</h3>
                <span className="card-icon">🎯</span>
              </div>
              <div className="card-value">{formatCurrency(dashboardStats.totalCosts.future_run_rate)}</div>
              <div className="card-subtitle">Post-optimization annual costs</div>
            </div>
          </div>
        </div>

        {/* Quick Actions for Master Data */}
        <div className="dashboard-section">
          <h2>Master Data Management</h2>
          <div className="actions-grid">
            <button 
              className="action-button"
              onClick={() => navigate('/customers/applications')}
            >
              <span className="action-icon">📱</span>
              <span>Application Master Data</span>
            </button>
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing')}
            >
              <span className="action-icon">💰</span>
              <span>Costing Overview</span>
            </button>
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing/current-run-rate')}
            >
              <span className="action-icon">📊</span>
              <span>Current Run Rates</span>
            </button>
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing/planned-projects')}
            >
              <span className="action-icon">📋</span>
              <span>Planned Projects</span>
            </button>
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing/migration-conversion')}
            >
              <span className="action-icon">🔄</span>
              <span>Migration Costs</span>
            </button>
            <button 
              className="action-button"
              onClick={() => navigate('/customers/costing/future-run-rates')}
            >
              <span className="action-icon">🎯</span>
              <span>Future Projections</span>
            </button>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MasterData;